// Module « pro » : Professionnels : équipes et attribution, portail propriétaire, validation des dépenses, actions groupées et rapports, artisans, import de portefeuille et intégrations, marque, baux commerciaux et professionnels.
import { Link } from "react-router";
import type { FrontModule } from "@core/modules";
import { date, money, percent } from "@core/format";
import { useGet } from "@core/hooks";
import { Card, ErrorBox, KeyValues, Loading, Notice } from "@core/ui";
import { ProDashboard, ProHomePage } from "./accueil";
import { ContractorPage, ContractorsPage } from "./artisans";
import { BillingPage, CommercialLeasePage, CommercialLeasesPage, NewCommercialLeasePage } from "./commercial";
import { LeaseTermsPanel } from "./conditions";
import { ExpensesPage } from "./depenses";
import { TeamPage } from "./equipe";
import { BulkPage, ReportsPage } from "./groupes";
import { ImportPage } from "./import";
import { IntegrationsPage } from "./integrations";
import { OwnersPage } from "./mandants";
import { BrandingPage } from "./marque";
import { ExpenseApprovalPage, PortalInvitationPage, PortalListPage, PortalPage } from "./portail";
import { OWNER_KINDS, accessState, Status } from "./shared";
import "./pro.css";

/** Onglet « Mandant » de la fiche logement. */
function PropertyMandantTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  const q = useGet<any>(`/api/v1/orgs/${orgId}/pro/properties/${propertyId}/mandant`);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <div className="stack">
      <Card title="Mandant">
        {d.owner ? (
          <KeyValues
            items={[
              ["Propriétaire", `${d.owner.display_name} (${OWNER_KINDS[d.owner.kind] ?? d.owner.kind})`],
              ["Quotes-parts", (d.shares as any[]).map((s) => `${s.display_name} ${String(s.share_pct).replace(".", ",")} %`).join(", ") || "—"],
              ["Portail", (d.access as any[]).length ? (d.access as any[]).map((a, i) => <Status key={i} kind="access" value={accessState(a)} />) : "Aucun accès"],
              [
                "Accord des dépenses",
                d.expense_rule ? `au-delà de ${money(d.expense_rule.threshold_cents)} (tolérance ${percent(d.expense_rule.tolerance_bp, 0)}, réponse sous ${d.expense_rule.response_days} jours)` : "Aucun seuil",
              ],
            ]}
          />
        ) : (
          <Notice>Aucun propriétaire n’est rattaché à ce logement.</Notice>
        )}
        <p className="small">
          <Link to={`/espace/${orgId}/professionnels/mandants`}>Gérer les mandants et leurs accès</Link>
        </p>
      </Card>
      <Card title="Équipe">
        {(d.assignments as any[]).length ? (
          <ul className="list-plain">
            {(d.assignments as any[]).map((a) => (
              <li key={a.id}>
                {a.full_name} · {a.role === "responsable" ? "Responsable" : "Suppléant"}
                {a.team ? ` · ${a.team}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucune personne attribuée à ce logement.</p>
        )}
      </Card>
      <Card title="Interventions d’artisans">
        {(d.interventions as any[]).length ? (
          <ul className="list-plain">
            {(d.interventions as any[]).map((i) => (
              <li key={i.id}>
                {date(i.performed_on)} · <Link to={`/espace/${orgId}/professionnels/artisans/${i.contractor_id}`}>{i.contractor}</Link> · {i.description}
                {i.amount_cents !== null ? ` · ${money(i.amount_cents)}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucune intervention enregistrée.</p>
        )}
      </Card>
    </div>
  );
}

function LeaseCommercialTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  return <LeaseTermsPanel orgId={orgId} leaseId={leaseId} />;
}

export const module: FrontModule = {
  name: "pro",
  orgRoutes: [
    { path: "professionnels", element: <ProHomePage /> },
    { path: "professionnels/equipe", element: <TeamPage /> },
    { path: "professionnels/mandants", element: <OwnersPage /> },
    { path: "professionnels/depenses", element: <ExpensesPage /> },
    { path: "professionnels/actions-groupees", element: <BulkPage /> },
    { path: "professionnels/rapports", element: <ReportsPage /> },
    { path: "professionnels/artisans", element: <ContractorsPage /> },
    { path: "professionnels/artisans/:contractorId", element: <ContractorPage /> },
    { path: "professionnels/import", element: <ImportPage /> },
    { path: "professionnels/integrations", element: <IntegrationsPage /> },
    { path: "professionnels/marque", element: <BrandingPage /> },
    { path: "professionnels/baux-commerciaux", element: <CommercialLeasesPage /> },
    { path: "professionnels/baux-commerciaux/nouveau", element: <NewCommercialLeasePage /> },
    { path: "professionnels/baux-commerciaux/:leaseId", element: <CommercialLeasePage /> },
    { path: "professionnels/facturation", element: <BillingPage /> },
  ],
  nav: [
    { to: "professionnels", label: "Vue d’ensemble", section: "Professionnels", perm: "read", order: 1 },
    { to: "professionnels/equipe", label: "Équipe", section: "Professionnels", perm: "read", order: 2, hideWhenRestricted: true },
    { to: "professionnels/mandants", label: "Propriétaires mandants", section: "Professionnels", perm: "read", order: 3, hideWhenRestricted: true },
    { to: "professionnels/depenses", label: "Validations de dépenses", section: "Professionnels", perm: "read", order: 4 },
    { to: "professionnels/actions-groupees", label: "Actions groupées", section: "Professionnels", perm: "operate", order: 5, hideWhenRestricted: true },
    { to: "professionnels/rapports", label: "Rapports", section: "Professionnels", perm: "read", order: 6, hideWhenRestricted: true },
    { to: "professionnels/artisans", label: "Artisans", section: "Professionnels", perm: "read", order: 7, hideWhenRestricted: true },
    { to: "professionnels/import", label: "Import", section: "Professionnels", perm: "operate", order: 8, hideWhenRestricted: true },
    { to: "professionnels/integrations", label: "Intégrations (API)", section: "Professionnels", perm: "members", order: 9, hideWhenRestricted: true },
    { to: "professionnels/marque", label: "Marque", section: "Professionnels", perm: "read", order: 10, hideWhenRestricted: true },
    { to: "professionnels/baux-commerciaux", label: "Baux commerciaux", section: "Professionnels", perm: "read", order: 11 },
    { to: "professionnels/facturation", label: "Facturation", section: "Professionnels", perm: "read", order: 12, hideWhenRestricted: true },
  ],
  publicRoutes: [{ path: "/validation-depense/:token", element: <ExpenseApprovalPage /> }],
  accountRoutes: [
    { path: "/moi/proprietaire", element: <PortalListPage /> },
    { path: "/moi/proprietaire/invitation/:token", element: <PortalInvitationPage /> },
    { path: "/moi/proprietaire/:ownerId", element: <PortalPage /> },
  ],
  accountNav: [{ to: "/moi/proprietaire", label: "Portail propriétaire" }],
  Dashboard: ProDashboard,
  propertyTabs: [{ id: "pro-mandant", label: "Mandant", order: 80, Component: PropertyMandantTab }],
  leaseTabs: [
    {
      id: "pro-conditions",
      label: "Conditions commerciales",
      order: 70,
      Component: LeaseCommercialTab,
      visible: ({ data }) => ["commercial", "professionnel"].includes(data?.lease?.regime),
    },
  ],
};
